import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";

const reviews = [
  { name: "Rajesh Kumar", role: "Residential Client", rating: 5, text: "Excellent architectural design and planning. Dhivisha Architects transformed our dream home into reality. Their attention to detail and understanding of space is remarkable. Highly recommended!", image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=300&q=60" },
  { name: "Priya Sundaram", role: "Interior Design Client", rating: 5, text: "We hired them for our office interior design and the result was beyond expectations. The team is very professional and delivered on time. The workspace feels modern and productive now.", image: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=300&q=60" },
  { name: "Karthik Murugan", role: "Residential Client", rating: 5, text: "Best registered architects in Kumbakonam! They designed our house with Vastu compliance and modern aesthetics. Very creative team with excellent communication throughout the project.", image: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=300&q=60" },
  { name: "Lakshmi Narayanan", role: "Commercial Client", rating: 5, text: "Very good experience with Dhivisha Architects. They handled our commercial building project efficiently. The 3D visualization helped us understand the design perfectly before construction.", image: "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=300&q=60" },
  { name: "Anitha Venkatesh", role: "Residential Client", rating: 5, text: "Outstanding work on our residential project! The team is very patient and incorporated all our requirements beautifully. The final result exceeded our expectations.", image: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=300&q=60" },
  { name: "Senthil Rajan", role: "Renovation Client", rating: 5, text: "Dhivisha Architects did an amazing job with our temple renovation. They perfectly balanced traditional architecture with modern structural integrity. Truly skilled professionals.", image: "https://images.unsplash.com/photo-1545558014-8692077e9b5c?w=300&q=60" },
  { name: "Meena Krishnan", role: "Interior Design Client", rating: 5, text: "Great interior design work for our restaurant. The ambience they created is wonderful and our customers love it. Good value for money and timely delivery.", image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=300&q=60" },
  { name: "Vignesh Balaji", role: "Residential Client", rating: 5, text: "Fantastic architects! They designed our duplex house and the elevation design is the talk of the neighbourhood. Very innovative and uses space very efficiently.", image: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=300&q=60" },
  { name: "Deepa Suresh", role: "Interior Design Client", rating: 5, text: "We are extremely happy with the interior work done for our apartment. The kitchen and living room designs are both beautiful and functional. Thank you Dhivisha team!", image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=300&q=60" },
  { name: "Arun Prakash", role: "Industrial Client", rating: 5, text: "Professional team with creative ideas. They handled our warehouse project with excellent planning. The structural design is solid and cost-effective. Will definitely work with them again.", image: "https://images.unsplash.com/photo-1565008447742-97f6f38c985c?w=300&q=60" },
  { name: "Bhuvaneshwari S", role: "Residential Client", rating: 5, text: "The pooja room interior they designed for our home is absolutely divine. Every detail was carefully planned. The team understood our religious requirements and created a beautiful sacred space.", image: "https://images.unsplash.com/photo-1523217582562-09d0def993a6?w=300&q=60" },
  { name: "Manikandan R", role: "Commercial Client", rating: 5, text: "They designed our showroom and the layout is perfect for customer flow. The lighting design and display arrangements are excellent. Our sales have improved since the renovation.", image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=300&q=60" },
  { name: "Kavitha Devi", role: "Interior Design Client", rating: 5, text: "Dhivisha team designed our entire flat interior including modular kitchen, wardrobes, and living area. The quality of work and finishing is top-notch. Very reasonable pricing too.", image: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=300&q=60" },
  { name: "Suresh Babu", role: "Residential Client", rating: 5, text: "We got our individual house designed by Dhivisha Architects. From plan approval to construction supervision, they handled everything smoothly. The elevation design is stunning!", image: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=300&q=60" },
  { name: "Revathi Mohan", role: "Interior Design Client", rating: 5, text: "Beautiful bedroom and hall interior work. They suggested colour combinations and materials that we never thought of. The 3D design they showed us matched the final output exactly.", image: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=300&q=60" },
  { name: "Ganesh Pandian", role: "Residential Client", rating: 5, text: "Excellent experience from start to finish. They designed our 2400 sq ft house with perfect ventilation and natural lighting. The Vastu-compliant design is exactly what we wanted.", image: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=300&q=60" },
  { name: "Saranya Kumar", role: "Commercial Client", rating: 5, text: "Our clinic was designed by Dhivisha Architects and patients always compliment the interiors. The waiting area is calming and the consultation rooms are well-planned. Excellent work!", image: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=300&q=60" },
  { name: "Vijayakumar T", role: "Renovation Client", rating: 5, text: "They renovated our 30-year-old house and it looks brand new now. The structural assessment was thorough and the modern upgrades blend perfectly with the original character. Highly satisfied!", image: "https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=300&q=60" },
];

const OVERALL_RATING = 4.6;
const TOTAL_REVIEWS = 163;
const CARDS_PER_VIEW = 3;

const StarRating = ({ rating }: { rating: number }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        size={16}
        className={s <= rating ? "text-yellow-400 fill-yellow-400" : "text-muted-foreground/30"}
      />
    ))}
  </div>
);

const GoogleReviews = () => {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(reviews.length / CARDS_PER_VIEW);

  const next = useCallback(() => {
    setPage((p) => (p + 1) % totalPages);
  }, [totalPages]);

  const prev = useCallback(() => {
    setPage((p) => (p - 1 + totalPages) % totalPages);
  }, [totalPages]);

  useEffect(() => {
    const interval = setInterval(next, 6000);
    return () => clearInterval(interval);
  }, [next]);

  const currentReviews = reviews.slice(page * CARDS_PER_VIEW, page * CARDS_PER_VIEW + CARDS_PER_VIEW);

  return (
    <section className="py-12 sm:py-20 bg-muted">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <motion.div
          className="text-center mb-8 sm:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-widest text-primary mb-2 sm:mb-3">Client Stories</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4 sm:mb-5">
            Hear From Our <span className="text-primary">Success Stories</span>
          </h2>

          {/* Google badge */}
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full border border-border bg-card shadow-sm mb-4">
            <svg viewBox="0 0 48 48" className="w-6 h-6 shrink-0">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            <span className="text-lg font-bold text-foreground">{OVERALL_RATING}</span>
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={s}
                  size={16}
                  className={s <= Math.round(OVERALL_RATING) ? "text-yellow-400 fill-yellow-400" : "text-yellow-400/30 fill-yellow-400/30"}
                />
              ))}
            </div>
            <span className="text-sm text-muted-foreground">({TOTAL_REVIEWS} Reviews)</span>
          </div>

          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Real stories from real clients who transformed their spaces with Dhivisha Architects.
          </p>
        </motion.div>

        {/* Cards slider */}
        <div className="relative">
          <button
            onClick={prev}
            className="absolute -left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-card border border-border shadow-md flex items-center justify-center hover:bg-primary hover:text-primary-foreground active:scale-95 transition-all hidden md:flex"
            aria-label="Previous reviews"
          >
            <ChevronLeft size={20} />
          </button>

          <div className="overflow-hidden md:mx-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={page}
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -60 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6"
              >
                {currentReviews.map((review, i) => (
                  <motion.div
                    key={review.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-card border border-border rounded-xl overflow-hidden shadow-sm hover:shadow-xl hover:border-primary/30 hover:-translate-y-1 transition-all duration-300 flex flex-col"
                  >
                    {/* Project image */}
                    <div className="h-40 overflow-hidden">
                      <img
                        src={review.image}
                        alt={`${review.name}'s project`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>

                    <div className="p-6 flex flex-col flex-1 justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
                            <Quote size={14} className="text-primary-foreground" />
                          </div>
                          <StarRating rating={review.rating} />
                        </div>
                        <p className="text-foreground leading-relaxed text-sm">
                          "{review.text}"
                        </p>
                      </div>

                      {/* Reviewer */}
                      <div className="flex items-center gap-3 mt-5 pt-4 border-t border-border">
                        <div className="w-9 h-9 rounded-full bg-muted border border-border flex items-center justify-center">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                            <circle cx="12" cy="7" r="4"/>
                          </svg>
                        </div>
                        <div>
                          <p className="font-semibold text-primary text-sm">{review.name}</p>
                          <p className="text-xs text-muted-foreground">{review.role}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          <button
            onClick={next}
            className="absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-card border border-border shadow-md flex items-center justify-center hover:bg-primary hover:text-primary-foreground active:scale-95 transition-all hidden md:flex"
            aria-label="Next reviews"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === page ? "bg-primary w-7" : "bg-foreground/20 hover:bg-foreground/40"}`}
              aria-label={`Go to page ${i + 1}`}
            />
          ))}
        </div>

        {/* Google link */}
        <div className="text-center mt-6">
          <a
            href="https://maps.app.goo.gl/GikirDGHwzid7Nq57"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline font-medium"
          >
            See all reviews on Google →
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
